import { FetchResult, useMutation } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { Mutations } from '@shared/api';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { EditableFieldRecord } from '../../types';
import { CardEntityError } from './useFormErrors';

type CreateMutationResult = FetchResult<API.CreateWorkflowEntityMutation>;

interface SaveNewEntityHooks {
  loading: boolean;
  errors: CardEntityError[];
  saveEntity: (fields: EditableFieldRecord) => Promise<CreateMutationResult>;
}

export function useSaveNewEntity(workflowTemplateId?: string | null): SaveNewEntityHooks {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const [createEntityMutation, { loading, error }] = useMutation<
    API.CreateWorkflowEntityMutation,
    API.CreateWorkflowEntityMutationVariables
  >(Mutations.entity.createWorkflowEntity);

  const saveEntity = useCallback(
    (fields: EditableFieldRecord) => {
      if (isNil(workflowTemplateId)) {
        return Promise.reject('Workflow template ID is required');
      }
      return createEntityMutation({
        variables: {
          input: {
            fields: toFieldJson(fields, workflowTemplate),
            workflowTemplateId: workflowTemplateId,
          },
        },
      });
    },
    [createEntityMutation, workflowTemplateId, workflowTemplate],
  );

  return {
    loading,
    errors: !isNil(error) ? [{ description: error.message }] : [],
    saveEntity,
  };
}
